import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Info} from "@phosphor-icons/react";
import { defaultInternship, internshipSchema } from "@reactive-resume/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Tooltip,
} from "@reactive-resume/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";
import { MonthYearDatePicker } from "../sections/shared/month-year-date-picker";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = internshipSchema;

type FormValues = z.infer<typeof formSchema>;

export const InternshipDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultInternship,
    resolver: zodResolver(formSchema),
  });

  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";

  return (
    <SectionDialog<FormValues> id="internship" form={form} defaultValues={defaultInternship}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="company"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel><span style={{fontSize: '14px'}}>Company Name</span></FormLabel>
              <FormControl>
                <URLInput
                  {...field}
                  value={
                    typeof field.value === "string"
                      ? { label: field.value, href: "" }
                      : (field.value ?? { label: "", href: "" })
                  }
                  placeholder="Company Name"
                  labelPlaceholder="Company Website"
                  disableAutoHttps={true}
                  swapLabelAndHref={true}
                  onChange={(value) => {
                    field.onChange(value);
                    form.setValue(
                      "url",
                      {
                        ...(typeof form.getValues("url") === "object" ? form.getValues("url") : {}),
                        href: value.href,
                      },
                      { shouldValidate: true },
                    );
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isMastersUnion && (
          <FormField
            name="companyDescription"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel><span style={{fontSize: '14px'}}>Company Description</span></FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Company description" maxLength={90} />
                </FormControl>
                <p className="text-xs text-muted-foreground">{`Character limit ${(field.value ?? "").length}/90`}</p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          name="position"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t({
                  message: "Position",
                  context: "Position held at a company, for example, Software Engineer",
                })}
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Position" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="location"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Location`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="City, Country" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Date or Date Range`}</FormLabel>
              <FormControl>
                <MonthYearDatePicker value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />


        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              {/* <FormLabel>{t`Summary`}</FormLabel> */}
              <FormLabel className="flex items-center gap-2">
                <span>{t`Summary`}</span>
                {isMastersUnion && (
                  <Tooltip content="Click on “Add More” to add more bullet points">
                    <span className="inline-flex cursor-help text-muted-foreground">
                      <Info size={14} />
                    </span>
                  </Tooltip>
                )}
              </FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <MastersUnionListEditor
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                ) : (
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
